import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

import styles from './Tooltip.module.less';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
}

const WIDGET_PADDING = 8;

export const Tooltip: React.FC<TooltipProps> = ({ content, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Keep tooltip within the widget bounds
  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (!isVisible || !tooltip) return;

    // Reset transform so we can measure the natural centered position
    tooltip.style.transform = 'translateX(-50%)';

    const tooltipRect = tooltip.getBoundingClientRect();
    const widgetEl = tooltip.closest('[class*="widgetContainer"]') as HTMLElement | null;
    if (!widgetEl) return;

    const widgetRect = widgetEl.getBoundingClientRect();
    const overflowLeft = (widgetRect.left + WIDGET_PADDING) - tooltipRect.left;
    const overflowRight = tooltipRect.right - (widgetRect.right - WIDGET_PADDING);

    if (overflowLeft > 0) {
      tooltip.style.transform = `translateX(calc(-50% + ${overflowLeft}px))`;
    } else if (overflowRight > 0) {
      tooltip.style.transform = `translateX(calc(-50% - ${overflowRight}px))`;
    }
  }, [isVisible]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  return (
    <div className={styles.tooltipContainer}>
      <div
        ref={triggerRef}
        className={styles.tooltipTrigger}
        onClick={handleClick}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible && (
        <div ref={tooltipRef} className={styles.tooltip}>
          {content}
          <div className={styles.arrow} />
        </div>
      )}
    </div>
  );
};
